import {
  CondenserType,
  ConfigurationType,
  ExistingSystemComponentType,
  ExistingSystemType,
  LevelType,
  SelectedSystemType
} from 'data/types';
import { buildDuctedDesignLayoutConfiguration } from 'utils/ducted-design-layout';
import {
  buildDuctedLayoutConfiguration,
  resolveLayoutExistingSystem,
  resolveLayoutExistingSystems
} from 'utils/ducted-layout-configuration';
import {
  COMPONENT_TYPE_TO_POSITION,
  ExistingEquipmentPositionSlot,
  isExistingEquipmentComponentPlaced
} from 'utils/existing-equipment';
import { getGridSpacesForLevel } from 'utils/grid';
import {
  buildLinesetForPair,
  calculateLinesetLengthFromPoints
} from 'utils/linesets';
import { WholeHouseDuctedConfigurationType } from 'utils/whole-house-ducted-configuration-types';

export const DUCTED_LINE_SET_LENGTH_BUFFER = 1.3;

const OUTDOOR_SLOTS: ExistingEquipmentPositionSlot[] = ['outdoor'];
const INDOOR_SLOTS: ExistingEquipmentPositionSlot[] = ['indoor', 'blower'];

export type ComputeDuctedLineSetRequiredLengthsParams = {
  system: SelectedSystemType;
  existingSystems: ExistingSystemType[];
  structure: LevelType[];
  configurations?: ConfigurationType[];
};

export type DuctedLineSetRequiredLengths = Record<string, number>;

const isPlacedInSlots = (
  component: ExistingSystemComponentType,
  slots: ExistingEquipmentPositionSlot[]
): boolean => {
  const slot = COMPONENT_TYPE_TO_POSITION[component.type];
  return (
    slot !== undefined &&
    slots.includes(slot) &&
    isExistingEquipmentComponentPlaced(component)
  );
};

export const hasPlacedLineSetEndpoints = (
  existingSystem: ExistingSystemType
): boolean =>
  existingSystem.components.some((component) =>
    isPlacedInSlots(component, OUTDOOR_SLOTS)
  ) &&
  existingSystem.components.some((component) =>
    isPlacedInSlots(component, INDOOR_SLOTS)
  );

function measureCondenserLineSet(
  condenser: CondenserType,
  layoutUniqueId: string,
  structure: LevelType[]
): number | null {
  const airHandler = condenser.zones[0]?.airHandler;
  if (!condenser.position || !airHandler) return null;

  const lineset = buildLinesetForPair(
    {
      uniqueId: condenser.uniqueId,
      levelUniqueId: condenser.levelUniqueId,
      position: condenser.position
    },
    {
      uniqueId: airHandler.uniqueId,
      levelUniqueId: airHandler.levelUniqueId,
      position: { x: airHandler.positionX, y: airHandler.positionY },
      targetSpaceId: undefined,
      airHandlerTypeCode: airHandler.airHandlerTypeCode
    },
    structure,
    (levelIndex) => getGridSpacesForLevel(structure, levelIndex),
    layoutUniqueId
  );

  const lengthFeet = calculateLinesetLengthFromPoints(lineset, structure);
  if (lengthFeet <= 0) return null;

  return Math.ceil(lengthFeet * DUCTED_LINE_SET_LENGTH_BUFFER);
}

function measureLayout(
  layout: WholeHouseDuctedConfigurationType,
  structure: LevelType[],
  isMeasurable: (condenser: CondenserType) => boolean
): DuctedLineSetRequiredLengths {
  const lengths: DuctedLineSetRequiredLengths = {};
  layout.condensers.forEach((condenser) => {
    if (!isMeasurable(condenser)) return;
    const length = measureCondenserLineSet(
      condenser,
      layout.uniqueId,
      structure
    );
    if (length !== null) lengths[condenser.uniqueId] = length;
  });
  return lengths;
}

export function computeDuctedLineSetRequiredLengths({
  system,
  existingSystems,
  structure,
  configurations = []
}: ComputeDuctedLineSetRequiredLengthsParams): DuctedLineSetRequiredLengths {
  const designLayout = buildDuctedDesignLayoutConfiguration({
    system,
    configurations
  });
  if (designLayout) return measureLayout(designLayout, structure, () => true);

  const pairSystems = resolveLayoutExistingSystems(system, existingSystems);
  const pairSystemByCondenser = new Map(
    system.configuration.condensers.map((condenser, index) => [
      condenser.uniqueId,
      pairSystems[index] ?? null
    ])
  );
  const measurable = system.configuration.condensers.filter((condenser) => {
    const pairSystem = pairSystemByCondenser.get(condenser.uniqueId);
    return !!pairSystem && hasPlacedLineSetEndpoints(pairSystem);
  });
  if (measurable.length === 0) return {};

  const layout = buildDuctedLayoutConfiguration({
    system,
    existingSystem: resolveLayoutExistingSystem(system, existingSystems),
    structure,
    existingSystems
  });
  if (!layout) return {};

  const measurableIds = new Set(measurable.map((c) => c.uniqueId));
  return measureLayout(layout, structure, (condenser) =>
    measurableIds.has(condenser.uniqueId)
  );
}
